import { useEffect, useRef } from 'react';
import { useWindowVirtualizer } from '@tanstack/react-virtual';

import { useGridCardSizeChecker } from 'components/pages/Grids/hooks/useGridCardSizeChecker/useGridCardSizeChecker';
import { useGridCardsViewItems } from 'components/pages/Grids/hooks/useGridCardsViewItems/useGridCardsViewItems';
import { checkTotalGridHeight } from 'components/pages/Grids/utils/checkTotalGridHeight';
import { useWidgetContext } from 'context/widgetProvider/widgetProvider';

import { OVER_SCAN } from '../../const';

let kSavedOffset: number | undefined = 0;
let prevTitle: string | undefined = '';

function useDynamicGridViewer() {
  const {
    imageHeight,
    imageWidth,
    settings,
    data: { items, isLoading, hasNextPage, fetchNextPage },
  } = useWidgetContext();
  const containerRef = useRef<HTMLDivElement | null>(null);
  const { columnCount, defaultHeight } = useGridCardSizeChecker();
  const isFullLoaded = !!items.length && !hasNextPage;

  checkTotalGridHeight(containerRef, isFullLoaded, items.length, columnCount, defaultHeight);

  const { gridItems } = useGridCardsViewItems({ items, columnCount, isFullLoaded });
  const virtualizer = useWindowVirtualizer({
    count: gridItems.length,
    estimateSize: () => defaultHeight, //  distance between rows
    overscan: OVER_SCAN,
    lanes: columnCount,
    initialOffset: settings.title === prevTitle ? kSavedOffset : 0, // for scroll restoration if the page title is the same
    onChange: (data) => {
      if (!data.isScrolling && items.length) {
        kSavedOffset = virtualizer.scrollOffset || undefined;
      }
    },
  });

  useEffect(() => {
    const lastItem = virtualizer.getVirtualItems().at(-1);

    if (!lastItem) {
      return;
    }

    if (lastItem.index >= items.length - 1 && hasNextPage && !isLoading && fetchNextPage) {
      fetchNextPage();
    }
  }, [hasNextPage, fetchNextPage, items.length, isLoading, virtualizer.getVirtualItems()]);

  useEffect(() => {
    const handleResize = () => {
      virtualizer.measure(); // Call measure on resize
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      prevTitle = settings.title; // set title of current page
    };
  }, [virtualizer]);

  return {
    isFullLoaded,
    gridItems,
    imageHeight,
    itemWidth: imageWidth,
    virtualizer,
  };
}

export { useDynamicGridViewer };
